// @flow
import React from 'react'
import { Link } from '@ta-interaktiv/contentbricks'
import AnchorLink from 'react-anchor-link-smooth-scroll'
import './styles.scss'

export class Methodology extends React.Component<Props, State> {
  constructor() {
    super()
    this.state = {
      isLimited: true
    }
  }

  showMore() {
    this.setState({ isLimited: false })
    // track in google analytics
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'Interactions',
      event_action: 'click',
      event_label: `show-more: Methodology`
    })
  }

  render() {
    return (
      <div className='method ui vertical segment dark' id='methodik'>
        <div
          className={
            this.state.isLimited
              ? 'limited ui text methodik container'
              : 'ui text methodik container'
          }
        >
          <div className='ta text'>
            <h4>Méthodologie et description du modèle</h4>

            <h5>Calcul du recul des glaciers</h5>
            <p>
              L'évolution des glaciers repose sur un modèle de calcul qui décrit
              les processus par lesquels la glace réagit à un changement
              climatique (
              <AnchorLink href='#foot-1' className='nav-link'>
                1
              </AnchorLink>
              ). D'une part, on calcule la quantité de neige qui tombe en chaque
              point du glacier, d'autre part la quantité qui fond en raison de
              températures plus élevées. Enfin, on détermine comment la masse de
              glace se déplace et se modifie sous l'effet de l'écoulement. Les
              calculs sont vérifiés à l'aide d'un grand nombre de données
              d'observation des dernières décennies, ce qui permet de faire des
              prévisions réalistes dans le futur. Bien que l'approche
              corresponde à l'état le plus récent de la science, les
              simplifications nécessaires entraînent certaines incertitudes dans
              les résultats. La méthode de calcul utilisée a été appliquée à
              tous les glaciers de Suisse (
              <AnchorLink href='#foot-2' className='nav-link'>
                2
              </AnchorLink>
              ), et cinq glaciers ont été sélectionnés.
            </p>
            <h5>Visualisation</h5>
            <p>
              Les visualisations se basent sur un modèle numérique de la surface
              du terrain, établi par l'Office fédéral de topographie
              (Swisstopo). L'étendue actuelle des glaciers est également
              utilisée (
              <AnchorLink href='#foot-3' className='nav-link'>
                3
              </AnchorLink>
              ). Tout d'abord, les glaciers sont entièrement retirés afin de
              mettre à nu la roche sous-jacente. L'épaisseur de la glace est
              connue grâce à des mesures effectuées avec un radar et à des
              modélisations (
              <AnchorLink href='#foot-4' className='nav-link'>
                4
              </AnchorLink>
              ). La forme du sous-sol libre de glace permet de déterminer
              l'emplacement probable des futurs lacs et ruisseaux. Le modèle de
              terrain est ensuite superposé à une photographie aérienne. L'image
              est traitée de manière à montrer des rochers et de l'eau plutôt
              que de la neige et de la glace. Enfin, la surface des glaciers,
              calculée pour chaque année de la période 2022-2100, est ajoutée en
              blanc au-dessus du terrain.
            </p>
            <div className='sources'>
              <h5>Sources des données</h5>
              <ul>
                <li>
                  Office fédéral de topographie swisstopo - swissAlti3D,
                  SWISSIMAGE
                </li>
                <li>
                  Région Lombardie - MNT 5X5 - Modello digitale del terreno (ed.
                  2015);{' '}
                  <Link
                    href='https://creativecommons.org/licenses/by/4.0/deed.it'
                    hideIcon
                  >
                    licence
                  </Link>
                </li>
                <li>
                  Région Vallée d'Aoste - Digital Terrain Model (DTM);{' '}
                  <Link
                    href='https://creativecommons.org/licenses/by/4.0/deed.it'
                    hideIcon
                  >
                    license
                  </Link>
                </li>
                <li>
                  Land Vorarlberg -{' '}
                  <Link href='https://data.vorarlberg.gv.at' hideIcon>
                    data.vorarlberg.gv.at
                  </Link>
                  ;{' '}
                  <Link
                    href='https://creativecommons.org/licenses/by/4.0/deed.de'
                    hideIcon
                  >
                    license
                  </Link>
                </li>
              </ul>
            </div>
            <h5>Évolution du climat</h5>
            <p>
              L'évolution future des glaciers est calculée pour deux scénarios
              climatiques au cours du 21ème siècle. Ces scénarios ont été
              définis par le Groupe d'experts intergouvernemental sur
              l'évolution du climat (GIEC). Le scénario optimiste{' '}
              <span className='ui green label small'>SSP1-1.9</span> décrit un
              monde dans lequel les émissions mondiales de CO<sub>2</sub> sont
              réduites à zéro net d'ici 2050 environ. Cela permettrait
              d'atteindre l'objectif de l'accord de Paris, à savoir limiter le
              réchauffement global causé par l'homme à 1,5 degré Celsius
              au-dessus des températures préindustrielles. Le scénario
              pessimiste <span className='ui red label'>SSP5-8.5</span> montre
              un avenir de croissance économique alimentée par la combustion de
              pétrole, de gaz et de charbon, avec un réchauffement mondial prévu
              de 4,4 degrés Celsius d'ici la fin du siècle.
            </p>
            <div className='references'>
              <h5>Références</h5>
              <p id='foot-1'>
                (1) Huss, M. and Hock, R. (2015). A new model for global glacier
                change and sea-level rise. Frontiers in Earth Science, 3:54,
                doi:10.3389/feart.2015.00054.
              </p>
              <p id='foot-2'>
                (2) Steffen, T., Huss, M., Estermann, R., Hodel, E., and
                Farinotti, D. (in review). Volume, formation and sedimentation
                of future glacier lakes in Switzerland. Earth Surface Dynamics
                Discussions, doi:10.5194/esurf-2022-12.
              </p>
              <p id='foot-3'>
                (3) Linsbauer, A., Huss, M., Hodel, E., Bauder, A., Fischer, M.,
                Weidmann, Y., Baertschi, H. and Schmassmann, E. (2021). The new
                Swiss Glacier Inventory SGI2016: From a topographic to a
                glaciological dataset. Frontiers in Earth Science, 9, 774,
                doi:10.3389/feart.2021.704189.
              </p>
              <p id='foot-4'>
                (4) Grab, M., Mattea, E., Bauder, B., Huss, M., Rabenstein, L.,
                Hodel, E., Linsbauer, A., Hellmann, S., Church, G., Langhammer,
                L., Schmid, L., Deleze, K, Schaer, P., Lathion, P., Farinotti,
                D., Maurer, H. (2021). Ice thickness distribution of all Swiss
                glaciers based on extended ground-penetrating radar data and
                glaciological modeling. Journal of Glaciology, 1-19,
                doi:10.1017/jog.2021.55.
              </p>
            </div>
          </div>
        </div>
        {this.state.isLimited && (
          <div className='showmore'>
            <button onClick={() => this.showMore()} className='ui button'>
              montrer davantage
            </button>
          </div>
        )}
      </div>
    )
  }
}
